import { AdUnitsImportValidationResults } from '@pubstack/common/src/adunit'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdUnits } from '~/api/adm-api.hook'
import { useToast } from '~/components/Toast/useToasts'
import { downloadCSVData } from '~/utils/fileDownload'

export const useAdUnitsImport = () => {
  // State
  const [adUnitsFile, setAdUnitsFile] = useState<File | null>()
  const [validationResults, setValidationResults] = useState<AdUnitsImportValidationResults>()
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [isValidating, setIsValidating] = useState<boolean>(false)
  const [isCreating, setIsCreating] = useState<boolean>(false)

  // Hooks
  const { validateImportFile, uploadImportFile } = useAdUnits(null)
  const navigate = useNavigate()
  const toast = useToast()

  const handleBackClick = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const handleFileChange = (e: File | null) => {
    setAdUnitsFile(e)
  }

  // No `useCallback`: the React Compiler memoizes this, and the `try` below puts the read of `adUnitsFile`
  // out of reach of its dependency inference, which then reads a hand-written dep list as an extra one.
  const handleDownload = async () => {
    setIsDownloading(true)
    // `finally`, because an empty file reads back as '' — falsy, so the early return used to leave the
    // control spinning for the life of the page.
    try {
      const data = await adUnitsFile?.text()

      if (!data) return

      downloadCSVData(data, adUnitsFile?.name)
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDelete = () => {
    setAdUnitsFile(null)
    setValidationResults(undefined)
  }

  const handleValidateFile = async () => {
    if (!adUnitsFile) return

    try {
      setIsValidating(true)

      const validationResults = await validateImportFile(adUnitsFile)

      setValidationResults(validationResults)
    } catch (_) {
      toast.alert('Something went wrong while validating ad units CSV file')
    } finally {
      setIsValidating(false)
    }
  }

  const handleCreate = async () => {
    if (!adUnitsFile) return

    try {
      setIsCreating(true)

      await uploadImportFile(adUnitsFile)

      toast.success('Ad units created')
      navigate('/monetization/one/adunits')
    } catch (_) {
      toast.alert('Something went wrong while uploading ad units CSV file')
    } finally {
      setIsCreating(false)
    }
  }

  return {
    adUnitsFile,
    validationResults,
    isDownloading,
    isValidating,
    isCreating,
    handleBackClick,
    handleFileChange,
    handleDownload,
    handleDelete,
    handleValidateFile,
    handleCreate,
  }
}
